import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { getRandomId } from '@lib/core';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'lib-button',
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  imports: [CommonModule, FontAwesomeModule],
  standalone: true
})
export class ButtonComponent {
  @Input() id: string;
  @Input() label: string;

  @Input() disabled = false;
  @Input() enableDelay = 0;
  @Input() keepSpinnerDuringDelay = true;

  @Input() spin = false;
  @Input() type: 'button' | 'submit' = 'button';
  @Input() styleClass: string | string[] | Set<string> | { [klass: string]: any };

  @Input() processingAriaLabel: string = 'Processing...';
  @Input() ariaLabel: string;

  inputId: string;

  @Output() buttonClick = new EventEmitter<void>();

  faSpinner = faSpinner;

  public internalDisabled = false;
  public internalSpin = false;
  private enableTimeout: any;
  private isDelayActive = false;
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.inputId = this.id ? `btn-${this.id}` : `btn-${getRandomId()}`;
    this.internalDisabled = this.disabled;
    this.internalSpin = this.spin;
  }

  ngOnDestroy() {
    this.clearEnableTimeout();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled']) {
      this.handleDisabledChange(changes['disabled'].currentValue);
    }

    if (changes['spin']) {
      this.handleSpinChange(changes['spin'].currentValue);
    }
  }

  private handleDisabledChange(newDisabledValue: boolean): void {
    this.clearEnableTimeout();

    if (newDisabledValue === true) {
      this.internalDisabled = true;
      this.isDelayActive = false;

      if (!this.spin) {
        this.internalSpin = false;
      }
    } else if (newDisabledValue === false && this.enableDelay > 0) {
      this.internalDisabled = true;
      this.isDelayActive = true;
      this.internalSpin = true;

      this.enableTimeout = setTimeout(() => {
        this.internalDisabled = false;
        this.isDelayActive = false;

        this.internalSpin = this.spin;
      }, this.enableDelay);
    } else {
      this.internalDisabled = false;
      this.isDelayActive = false;
      this.internalSpin = this.spin;
    }
  }

  private handleSpinChange(newSpinValue: boolean): void {
    if (this.isDelayActive) {
      return;
    }

    this.internalSpin = newSpinValue;
  }

  private clearEnableTimeout(): void {
    if (this.enableTimeout) {
      clearTimeout(this.enableTimeout);
      this.enableTimeout = null;
    }
  }

  get computedAriaLabel(): string {
    if (this.showSpinner) {
      return this.processingAriaLabel;
    }
    return this.ariaLabel || this.label || '';
  }

  get isDisabled(): boolean {
    return this.internalDisabled || this.internalSpin;
  }

  get showSpinner(): boolean {
    return this.internalSpin || (this.keepSpinnerDuringDelay && this.internalDisabled && this.spin);
  }

  onButtonClick() {
    if (!this.isDisabled) {
      this.buttonClick.emit();
    }
  }
}
